// restaurantAdapter.ts
//
// Compatibility layer between the new `zapee_restaurant_partner` collection
// (nested schema) and the flat restaurant model the app's screens already use.
//
//   flattenRestaurant()  — nested Firestore doc  → flat object (for reads)
//   toNestedRestaurant() — flat form values      → nested payload (for writes)
//
// Writes are applied with setDoc({merge:true}) so unmapped nested sub-fields
// (operatingHours, cuisines, offers, legal, rating counts, …) are preserved.

export const RESTAURANT_COLLECTION = 'zapee_restaurant_partner';

const num = (v: any, d = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? d : n;
};

/** Nested `zapee_restaurant_partner` doc → flat shape the screens read. */
export function flattenRestaurant(docId: string, data: any): any {
  const loc = data.location || {};
  const del = data.delivery || {};
  const pricing = data.pricing || {};
  const media = data.media || {};
  const rating = data.rating || {};
  const badges = data.badges || {};

  return {
    docId,
    id: data.id || data.uid || docId,
    uid: data.uid || docId,
    storeuid: data.storeuid || data.uid || docId,
    storename: data.storename || data.name || '',
    category: data.category || '',
    // Menu categories live on the restaurant doc — pass them through so the
    // admin product screen can build its category filter/picker from them.
    categories:
      data.categories ?? data.menuCategories ?? data.categoryList ??
      (data.menu && data.menu.categories) ?? [],
    phone: data.phone || '',
    email: data.email || '',
    branchId: data.branchId || '',

    // location.*
    address: loc.address ?? data.address ?? '',
    sublocality: loc.sublocality ?? data.sublocality ?? '',
    pincode: loc.pincode ?? data.pincode ?? '',
    mapaddress: loc.mapAddress ?? data.mapaddress ?? '',
    mappin: loc.mapPin ?? data.mappin ?? '',
    storeLat: String(loc.latitude ?? data.storeLat ?? ''),
    storeLon: String(loc.longitude ?? data.storeLon ?? ''),

    // delivery.*
    delivery: num(del.minCharge ?? data.delivery),
    kmcharge: num(del.perKmCharge ?? data.kmcharge),
    range: num(del.maxRange ?? data.range),

    // pricing.*
    packing: num(pricing.packingCharge ?? data.packing),
    tax: num(pricing.taxRate ?? data.tax),
    service: num(pricing.platformFeeRate ?? data.service),
    minAmount: num(pricing.minOrderAmount ?? data.minAmount),

    // content
    feature: data.feature || '',
    announcement: data.announcement || '',
    policy: data.policy || '',
    details: data.description ?? data.details ?? '',

    // media.*
    image: media.logo || data.image || '',
    bannerImages: media.bannerImages || data.bannerImages || [],

    // status
    active: data.active ?? true,
    online: data.online ?? false,
    show: data.show ?? true,
    premium: badges.premium ?? data.premium ?? false,
    rating: num(rating.average ?? data.rating),

    createdTimestamp: data.createdAt ?? data.createdTimestamp,
  };
}

/** Map an array of Firestore snapshots to flat restaurant objects. */
export function flattenRestaurantDocs(docs: { id: string; data: () => any }[]): any[] {
  return docs.map(d => flattenRestaurant(d.id, d.data()));
}

const DEFAULT_HOURS = () => {
  const day = { open: '09:00', close: '23:00', closed: false };
  return {
    monday: { ...day }, tuesday: { ...day }, wednesday: { ...day }, thursday: { ...day },
    friday: { ...day }, saturday: { ...day }, sunday: { ...day },
  };
};

/**
 * Flat form values → nested payload for the new collection.
 * Use with setDoc(ref, payload, { merge: true }) to preserve untouched
 * nested sub-fields. Pass `isNew` to seed defaults for brand-new docs.
 */
export function toNestedRestaurant(flat: any, isNew = false): any {
  const payload: any = {
    storename: flat.storename ?? '',
    name: flat.storename ?? '',
    storeuid: flat.storeuid ?? flat.uid ?? flat.id ?? '',
    uid: flat.uid ?? flat.id ?? '',
    id: flat.id ?? flat.uid ?? '',
    branchId: flat.branchId ?? '',
    phone: flat.phone ?? '',
    email: flat.email ?? '',
    category: flat.category ?? '',
    feature: flat.feature ?? '',
    announcement: flat.announcement ?? '',
    policy: flat.policy ?? '',
    description: flat.details ?? '',
    active: !!flat.active,
    online: !!flat.online,
    show: flat.show ?? true,
    image: flat.image || '',
    bannerImages: Array.isArray(flat.bannerImages) ? flat.bannerImages : [],

    location: {
      address: flat.address || '',
      sublocality: flat.sublocality || '',
      pincode: flat.pincode || '',
      mapAddress: flat.mapaddress || '',
      mapPin: flat.mappin || '',
      latitude: num(flat.storeLat),
      longitude: num(flat.storeLon),
    },
    delivery: {
      minCharge: num(flat.delivery),
      perKmCharge: num(flat.kmcharge),
      maxRange: num(flat.range),
    },
    pricing: {
      packingCharge: num(flat.packing),
      taxRate: num(flat.tax),
      platformFeeRate: num(flat.service),
      minOrderAmount: num(flat.minAmount),
    },
    media: {
      logo: flat.image || '',
      bannerImages: Array.isArray(flat.bannerImages) ? flat.bannerImages : [],
    },
    badges: {
      premium: !!flat.premium,
    },
    rating: {
      average: num(flat.rating),
    },
  };

  if (isNew) {
    payload.operatingHours = DEFAULT_HOURS();
    payload.cuisines = [];
    payload.categories = [];
    payload.offers = [];
    payload.rating = { average: num(flat.rating), totalRatings: 0, totalReviews: 0 };
  }

  return payload;
}
